<template>
  <div class="site-header-container">
    <header
      ref="siteHeader"
      class="site-header"
      :class="[
        $class(headerState),
        {
          [$class('transparent')]: isTransparent,
        },
      ]"
      data-sign-post-priority="1"
      :js-sign-post="$string('accessibility.sign_posts.site_header')"
    >
      <pre-header-banner
        v-if="showPreHeaderBanner"
        :background-color="preHeaderBannerBackground"
        :banners="preHeaderBannerContent"
        :text-color="preHeaderBannerTextColour"
      />
      <div
        ref="masthead"
        class="site-header__masthead"
      >
        <div
          class="site-header__container"
          js-site-header="container"
        >
          <div class="site-header__menu-nav">
            <button
              v-if="showNavigation"
              class="site-header__menu"
              :aria-label="$string('general.site_header.menu')"
              @click="toggleOverlay({
                component: 'menu-drawer',
                ignoreDismissed: true,
                namespace: 'menuDrawer',
              })"
              @mousedown="loadComponent('menu-drawer')"
              @touchstart="loadComponent('menu-drawer')"
            >
              <icon-menu />
            </button>

            <nav
              v-if="showNavigation"
              class="
                site-header__navigation
                site-header__navigation--primary
              "
              :aria-label="$string('accessibility.sign_posts.site_header')"
            >
              <ul class="site-header__navigation-list">
                <li
                  v-for="(tier1) in navigation.slice(0, linkItemCount)"
                  :key="tier1.handle"
                  class="site-header__navigation-item"
                >
                  <a
                    class="site-header__navigation-link text-body-2xs"
                    :href="tier1.url"
                    @click="handleLinkClick($event, tier1)"
                    v-text="tier1.title"
                  />
                </li>
              </ul>
            </nav>
          </div>

          <!-- Header logo -->
          <a
            class="site-header__logo"
            :href="$variable('routes.root')"
            :aria-label="$string('general.site_header.return_home')"
          >
            <transition
              name="fade-down"
              mode="out-in"
            >
              <component
                :is="titleElement"
                v-if="headerState === 'open'"
                id="site-header-title"
                class="site-header__logo-image"
                v-html="$variable('store.logo_primary')"
              />

              <component
                :is="titleElement"
                v-else
                id="site-header-title"
                class="site-header__logo-image is-alt"
                v-html="$variable('store.logo_secondary')"
              />
            </transition>
          </a>

          <nav
            class="
              site-header__navigation
              site-header__navigation--additional
            "
            :aria-label="$string('accessibility.sign_posts.site_header_additional')"
          >
            <ul class="site-header__navigation-list">
              <li
                class="
                  site-header__navigation-item
                  site-header__navigation-item--desktop
                "
              >
                <market-switcher-trigger class="site-header__navigation-link" />
              </li>
              <li
                v-if="showSearch"
                class="
                  site-header__navigation-item
                  site-header__navigation-item--account
                "
              >
                <a
                  class="site-header__navigation-link"
                  :href="$variable('routes.search')"
                  :aria-label="createAriaLabel('search')"
                  @click.prevent="openSearch"
                >
                  <icon-search />
                </a>
              </li>

              <li
                v-if="showAccount"
                class="
                  site-header__navigation-item
                  site-header__navigation-item--desktop
                "
              >
                <a
                  class="site-header__navigation-link"
                  :href="$variable('routes.account.url')"
                  :aria-label="createAriaLabel('account')"
                >
                  <icon-account />
                </a>
              </li>

              <li
                v-if="showCart"
                class="site-header__navigation-item site-header__navigation-item-cart"
              >
                <a
                  class="site-header__navigation-link"
                  :href="$variable('routes.cart.url')"
                  :aria-label="createAriaLabel('cart')"
                  @click="handleCartToggle"
                >
                  <icon-bag />
                </a>
                <transition name="fade-down">
                  <span
                    v-if="cart.item_count > 0"
                    aria-hidden="true"
                    class="site-header__cart-badge"
                    v-text="cart.item_count"
                  />
                </transition>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <secondary-header
        v-if="secondaryLinks.length > 0"
        :header-height="height.total"
        :links="secondaryLinks"
      />
    </header>

    <overlay
      v-if="showNavigation"
      key="menuDrawer"
      block-class="menu-drawer-overlay"
      :direction="transitionDirection"
      namespace="menuDrawer"
      :teleport="false"
      :template="false"
      :transition-delay="transitionDelay"
      type="drawer"
      @overlay-open-transition-complete="openComplete"
    >
      <template #body="{ isActive }">
        <menu-drawer
          v-if="getComponentVisible('menu-drawer')"
          v-cloak
          :key="drawerKey"
          ref="menuDrawer"
          :drawer-lower-image="drawerLowerImage"
          :drawer-lower-links="drawerLowerLinks"
          :add-sale-highlight="addSaleHighlight"
          :handle="drawerNavigationMenu"
          :is-active="isActive"
          :images="images"
          :navigation="navigation"
          :active-menu="activeMenu"
          :popular-searches="popularSearches"
          :show-drawer-account="showDrawerAccount"
          namespace="menuDrawer"
          @clear-active-menu="() => activeMenu = null"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Component / Global: Site header
 * -----------------------------------------------------------------------------
 * Site header component, contains menu drawer and meganav.
 * - No style import as it's included in critical stylesheet.
 *
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

import PreHeaderBanner from './pre-header-banner'
import MarketSwitcherTrigger from '~async/market-switcher-trigger/market-switcher-trigger'
import SecondaryHeader from '~async/secondary-header/secondary-header'

import IconSearch from '~icons/misc/search.svg'
import IconAccount from '~icons/misc/account.svg'
import IconBag from '~icons/misc/bag.svg'
import IconMenu from '~icons/misc/menu.svg'

export default {
  name: 'SiteHeader',

  components: {
    IconSearch,
    IconAccount,
    IconBag,
    IconMenu,
    PreHeaderBanner,
    SecondaryHeader,
    MarketSwitcherTrigger,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackPrefetch: true */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
  },

  props: {
    addSaleHighlight: {
      type: Boolean,
      default: false,
    },
    drawerLowerImage: {
      type: String,
      default: '',
    },
    drawerLowerLinks: {
      type: Array,
      default: () => [],
    },
    drawerNavigationMenu: {
      type: String,
      default: 'main-menu',
    },
    images: {
      type: Array,
      default: () => ([]),
    },
    navigation: {
      type: Array,
      default: () => [],
    },
    preHeaderBannerBackground: {
      type: String,
      default: '',
    },
    preHeaderBannerTextColour: {
      type: String,
      default: '',
    },
    preHeaderBannerContent: {
      type: Array,
      default: () => [],
    },
    popularSearches: {
      type: Array,
      default: () => [],
    },
    showAccount: {
      type: Boolean,
      default: false,
    },
    showCart: {
      type: Boolean,
      default: false,
    },
    showSearch: {
      type: Boolean,
      default: false,
    },
    showNavigation: {
      type: Boolean,
      default: false,
    },
    showDrawerAccount: {
      type: Boolean,
      default: true,
    },
    titleElement: {
      type: String,
      default: 'h1',
    },
    transparencyEnabled: {
      type: Boolean,
      default: false,
    },
    showPreHeaderBanner: {
      type: Boolean,
      default: true,
    },
    linkItemCount: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      activeMenu: null,
      drawerKey: 0,
      height: {
        masthead: 0,
        total: 0,
        visible: 0,
      },
      isTransparent: false,
      searchFocus: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      breakpoint: (state) => state.index.screen.breakpoint,
      cart: (state) => state.cart.response,
      cartHistory: (state) => state.cart.history,
      headerState: (state) => state.index.scroll.header,
      loadedStylesheets: (state) => state.index.stylesheets,
      settings: (state) => state.index.settings,
      secondaryLinks: (state) => state.header.secondaryLinks,
    }),

    /**
     * Changes the drawer transition direction.
     * @returns {String}
     */
    transitionDirection()  {
      return this.activeMenu ? 'right' : 'up'
    },

    /**
     * Adds a very slight delay to transition animations.
     * @returns {Number}
     */
    transitionDelay()  {
      return this.activeMenu ? this.$timing('normal', { unit: 's' }) : 0
    },
  },

  watch: {

    /**
     * Watch header state to update height variables.
     * - Wait for site header transition to end before updating height.
     * - Set height immediately if prefers reduced motion.
     */
    headerState() {
      if (this.headerState === 'open') {
        this.isTransparent = this.transparencyEnabled
      }

      if (this.headerState === 'collapsed') {
        this.isTransparent = false
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setHeights()
        return
      }

      this.$refs.siteHeader.ontransitionend = (args) => {
        if (!args || args.propertyName !== 'transform') {
          return
        }

        this.setHeights()
      }
    },

    /**
     * Watch total height to update CSS variables.
     */
    'height.total': function() {
      document.documentElement.style.setProperty(
        '--header-height',
        `${this.height.total}px`,
      )
    },

    /**
     * Watch visible height to update CSS variable.
     */
    'height.visible': function() {
      document.documentElement.style.setProperty(
        '--header-visible-height',
        `${this.height.visible}px`,
      )
    },

    /**
     * Watch loaded stylesheets to set height.
     */
    loadedStylesheets() {
      if (!this.loadedStylesheets.includes('layout.theme.css')) {
        return
      }

      /**
       * Delay set height to wait for CSS to be parsed.
       */
      setTimeout(() => {
        this.setHeights()
      }, this.$timing('quick'))
    },

    /**
     * Ensure header transparency is correct when the setting is changed.
     */
    transparencyEnabled() {
      if (!this.transparencyEnabled) {
        return
      }

      if (window.scrollY < this.settings.start) {
        this.isTransparent = true
      }
    },
  },

  async mounted() {
    await this.$nextTick()
    this.setHeights(true)
    this.isTransparent = this.transparencyEnabled

    cnvs.EventBus.listen('cnvs:resize', () => {
      this.setHeights(true)
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      loadComponent: 'components/load',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Helper to consyruct an aria-label
     * @param {String} link- String identifying the icon/button.
     * @returns {String} - Constructed aria-label string.
     */
    createAriaLabel(link) {
      return `${this.$string('accessibility.open')} ${link} ${this.$string('general.site_header.icon_button')}`
    },

    /**
     * Set heights of header.
     * - Height.total is used for body padding.
     * - Height.visible is used for overlays etc.
     * @param {Boolean} mounted - If height is being set on mounted.
     */
    setHeights(mounted) {
      if (mounted) {
        this.height.total = this.$refs.siteHeader.offsetHeight
      }

      this.height.visible = (this.headerState === 'closed')
        ? 0
        : this.$refs.masthead.offsetHeight
    },

    /**
     * Handle link click.
     * @param {Object|Boolean} link - Clicked link object, or false.
     */
    handleLinkClick(event, link) {
      if (link.links.length < 1) { return }

      event.preventDefault()
      this.activeMenu = link.title

      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Handle the open search link click.
     * @param {Object} event - Click event.
     */
    openSearch(event) {
      event.preventDefault()
      this.searchFocus = true

      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Reacts to the end of drawer open transition.
     */
    openComplete() {

      /**
       * No early return as we can fire further events as required when drawer opens
       */
      if (this.searchFocus) {
        setTimeout(() => {
          const searchInput = document.querySelector('.menu-drawer [js-predictive-search="input"]')
          if (!searchInput) {
            return
          }

          searchInput.focus()
          this.searchFocus = false
        }, this.$timing('normal'))
      }
    },

    /**
     * Handle cart toggle.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },
  },
}
</script>
